import React, { useState, useEffect, useCallback } from 'react';
import { 
  Phone, PhoneIncoming, PhoneOutgoing, Clock, User, 
  MapPin, Search, RefreshCw, Calendar, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CallLog = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('today');

  const fetchCalls = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      let startDate = new Date();
      if (dateFilter === 'today') {
        startDate.setHours(0, 0, 0, 0);
      } else if (dateFilter === 'week') {
        startDate.setDate(startDate.getDate() - 7);
      } else if (dateFilter === 'month') {
        startDate.setMonth(startDate.getMonth() - 1);
      }

      const response = await fetch(
        `${API_URL}/api/calls?start_date=${startDate.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setCalls(data);
      }
    } catch (error) {
      console.error('Error fetching calls:', error);
    } finally {
      setLoading(false);
    }
  }, [dateFilter]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  const filteredCalls = calls.filter(call => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        call.customer_name?.toLowerCase().includes(query) ||
        call.customer_phone?.includes(query) ||
        call.pickup_address?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const stats = {
    total: calls.length,
    withBooking: calls.filter(c => c.ride_id).length,
    withoutBooking: calls.filter(c => !c.ride_id).length
  };

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Phone className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-500">Gjithsej Telefonata</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <PhoneIncoming className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{stats.withBooking}</div>
              <div className="text-sm text-gray-500">Me Porosi</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <PhoneOutgoing className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-500">{stats.withoutBooking}</div>
              <div className="text-sm text-gray-500">Pa Porosi</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-2">
          {[
            { value: 'today', label: 'Sot' },
            { value: 'week', label: 'Java' },
            { value: 'month', label: 'Muaji' },
            { value: 'all', label: 'Të Gjitha' }
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setDateFilter(f.value)}
              className={`px-4 py-2 rounded-xl font-medium transition ${
                dateFilter === f.value 
                  ? 'bg-red-600 text-white' 
                  : 'bg-white text-gray-700 border hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Kërko..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-red-500 w-64"
            />
          </div>
          <button
            onClick={fetchCalls}
            className="p-2 bg-white border rounded-xl hover:bg-gray-50"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Call List */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
          </div>
        ) : filteredCalls.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {filteredCalls.map(call => (
              <div key={call.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    call.ride_id ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {call.ride_id ? (
                      <PhoneIncoming className="w-5 h-5 text-green-600" />
                    ) : (
                      <Phone className="w-5 h-5 text-gray-500" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900">
                        {call.customer_name || 'Klient i panjohur'}
                      </span>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-600">{call.customer_phone}</span>
                    </div>

                    {call.pickup_address && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <MapPin className="w-4 h-4 text-green-500" />
                        <span>{call.pickup_address}</span>
                      </div>
                    )}

                    {call.dropoff_address && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <MapPin className="w-4 h-4 text-red-500" />
                        <span>{call.dropoff_address}</span>
                      </div>
                    )}

                    {call.notes && (
                      <p className="mt-1 text-sm text-gray-500 italic">"{call.notes}"</p>
                    )}
                  </div>

                  {/* Time & Status */}
                  <div className="text-right">
                    <div className="text-sm text-gray-500 flex items-center gap-1 justify-end">
                      <Clock className="w-4 h-4" />
                      {new Date(call.created_at).toLocaleString('sq-AL', {
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short'
                      })}
                    </div>
                    {call.ride_id ? (
                      <span className="mt-1 inline-block px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        Porosi e krijuar
                      </span>
                    ) : (
                      <span className="mt-1 inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        Pa porosi
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <Phone className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Nuk ka telefonata të regjistruara</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallLog;
